/**
 * useWebSocket - Real-time WebSocket connection hook for Belonging Sessions
 * 
 * Features:
 * - Automatic connection management
 * - Reconnection with exponential backoff
 * - Heartbeat/ping-pong to keep connection alive
 * - Message type handlers
 * - Connection status tracking
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Convert HTTP URL to WebSocket URL
const getWebSocketUrl = (userId, userName) => {
  // Handle different URL formats
  let baseUrl = BACKEND_URL;
  
  // Convert https to wss, http to ws
  baseUrl = baseUrl.replace(/^https/, 'wss').replace(/^http/, 'ws');
  
  // Remove trailing /api if present
  baseUrl = baseUrl.replace(/\/api\/?$/, '');
  
  // Build WebSocket URL
  const wsUrl = `${baseUrl}/api/ws/${encodeURIComponent(userId)}?user_name=${encodeURIComponent(userName)}`;
  
  return wsUrl;
};

// Connection states
export const ConnectionState = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

/**
 * Custom hook for WebSocket connection
 * @param {Object} user - User object with _id and name
 * @param {Object} options - Configuration options
 */
export function useWebSocket(user, options = {}) {
  const {
    autoConnect = true,
    reconnectAttempts = 5,
    reconnectInterval = 3000,
    heartbeatInterval = 25000,
    onMessage = () => {},
    onEmpathyAlert = () => {},
    onNotification = () => {},
    onNewMessage = () => {},
    onUserStatus = () => {},
    onConnectionChange = () => {}
  } = options;

  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
  const [lastMessage, setLastMessage] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  
  const wsRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const heartbeatIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Update connection state and notify
  const updateConnectionState = useCallback((state) => {
    setConnectionState(state);
    onConnectionChange(state);
  }, [onConnectionChange]);

  // Send a message through WebSocket
  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    console.warn('WebSocket not connected, cannot send message');
    return false;
  }, []);

  // Send heartbeat ping
  const sendHeartbeat = useCallback(() => {
    sendMessage({ type: 'ping' });
  }, [sendMessage]);

  // Join a room (for group practice)
  const joinRoom = useCallback((roomId) => {
    return sendMessage({ type: 'subscribe_room', room_id: roomId });
  }, [sendMessage]);

  // Leave a room
  const leaveRoom = useCallback((roomId) => {
    return sendMessage({ type: 'unsubscribe_room', room_id: roomId });
  }, [sendMessage]);

  // Send a message to a room
  const sendRoomMessage = useCallback((roomId, content) => {
    return sendMessage({ 
      type: 'room_message', 
      room_id: roomId, 
      content 
    });
  }, [sendMessage]);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      setLastMessage(data);
      
      // Route message to appropriate handler
      switch (data.type) {
        case 'connection':
          console.log('[WebSocket] Connected:', data.message);
          break;
          
        case 'pong':
          // Heartbeat response received
          break;
          
        case 'empathy_alert':
          console.log('[WebSocket] Empathy Alert received:', data.alert);
          onEmpathyAlert(data.alert);
          break;
          
        case 'notification':
          console.log('[WebSocket] Notification:', data.notification);
          onNotification(data.notification);
          break;
          
        case 'new_message':
          console.log('[WebSocket] New message from:', data.sender_name);
          onNewMessage(data);
          break;
          
        case 'user_status':
          console.log('[WebSocket] User status:', data.user_id, data.status);
          onUserStatus(data);
          // Update online users list
          if (data.status === 'online') {
            setOnlineUsers(prev => {
              if (!prev.find(u => u.user_id === data.user_id)) {
                return [...prev, { user_id: data.user_id, user_name: data.user_name }];
              }
              return prev;
            });
          } else {
            setOnlineUsers(prev => prev.filter(u => u.user_id !== data.user_id));
          }
          break;
          
        case 'room_message':
        case 'room_join':
        case 'room_leave':
        case 'room_subscribed':
        case 'room_unsubscribed':
          // Room-related messages
          onMessage(data);
          break;
          
        case 'broadcast':
          console.log('[WebSocket] Broadcast:', data.message);
          onMessage(data);
          break;
          
        default:
          onMessage(data);
      }
    } catch (error) {
      console.error('[WebSocket] Failed to parse message:', error);
    }
  }, [onEmpathyAlert, onNotification, onNewMessage, onUserStatus, onMessage]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!user?._id) {
      console.warn('[WebSocket] Cannot connect without user ID');
      return;
    }

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    updateConnectionState(ConnectionState.CONNECTING);
    
    const wsUrl = getWebSocketUrl(user._id, user.name || 'User');
    console.log('[WebSocket] Connecting to:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WebSocket] Connected successfully');
        updateConnectionState(ConnectionState.CONNECTED);
        reconnectCountRef.current = 0;
        
        // Start heartbeat
        heartbeatIntervalRef.current = setInterval(sendHeartbeat, heartbeatInterval);
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        updateConnectionState(ConnectionState.ERROR);
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        updateConnectionState(ConnectionState.DISCONNECTED);
        
        // Clear heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        
        // Attempt reconnection if not intentional close
        if (event.code !== 1000 && reconnectCountRef.current < reconnectAttempts) {
          reconnectCountRef.current += 1;
          const delay = reconnectInterval * Math.pow(1.5, reconnectCountRef.current - 1);
          console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectCountRef.current})`);
          updateConnectionState(ConnectionState.RECONNECTING);
          
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      updateConnectionState(ConnectionState.ERROR);
    }
  }, [user, updateConnectionState, handleMessage, sendHeartbeat, heartbeatInterval, reconnectAttempts, reconnectInterval]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('[WebSocket] Disconnecting...');
    
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Clear heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    // Close connection
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    
    reconnectCountRef.current = reconnectAttempts; // Prevent auto-reconnect
    updateConnectionState(ConnectionState.DISCONNECTED);
  }, [reconnectAttempts, updateConnectionState]);

  // Auto-connect when user is available
  useEffect(() => {
    if (autoConnect && user?._id) {
      connect();
    }
    
    return () => {
      disconnect();
    };
    // eslint-disable-next-line
  }, [user?._id, autoConnect]);

  return {
    // State
    connectionState,
    isConnected: connectionState === ConnectionState.CONNECTED,
    lastMessage,
    onlineUsers,
    
    // Actions
    connect,
    disconnect,
    sendMessage,
    joinRoom,
    leaveRoom,
    sendRoomMessage
  };
}

export default useWebSocket;
